/**
 * using a custom type in a Vue->props declaration produces
 * a console warning in dev envs (if the data is de-serialized
 * json) b/c Vue checks the type with instanceof. It's worth
 * getting the console warning to get auto-complete, etc.
 */

// todo: write tests
import types from './index';

export const productHelpers = {
  getLocalization(product, language) {
    if (!isNaN(language)) {
      language = { language_id: language };
    }

    let matchArray = product.localizations.filter((l) => {
      return l.language_id === language.language_id
    });
    if (matchArray.length > 0) return new types.Localization(matchArray[0]);
    else return new types.Localization({
      language_id: language.language_id,
      product_id: product.product_id,
      language: language,
    });
  },
  getPreferredName(product) {
    try {
      let localization = this.getLocalization(product, 1);
      return localization.product_name_tm || localization.product_name || product.localizations[0].product_name_tm || product.localizations[0].product_name;
    } catch (e) {
      return '?';
    }
  },
  getLanguages(product) {
    return product.localizations
      .map((localization) => {
        return localization.language
      })
      .sort((lang1, lang2) => {
        return lang1.language_id - lang2.language_id
      });
  },
  getImage(product, type = 'product', size = 'regular') {
    try {
      return `${product.images.filter((image) => {
        return image.image.image_type.name === type;
      }).filter((image) => {
        return image.image.image_size.name === size;
      })[0].image.url}?ts=${Date.now()}`;
    } catch (e) {
      try {
        return `${product.images.filter((image) => {
          return image.image.image_type.name === type;
        })[0].image.url}?ts=${Date.now()}`;
      } catch (e) {
        if (type === 'product') return 'https://www.revereelectric.com/ASSETS/WEB_THEMES//REVERE_ELECTRIC/images/NoImage.png';
        else return 'https://www.revereelectric.com/ASSETS/WEB_THEMES//REVERE_ELECTRIC/images/NoImage.png';//https://www.revereelectric.com/ASSETS/WEB_THEMES//REVERE_ELECTRIC/images/NoImage.png
      }
    }
  },
};

export const productFrequencyHelpers = {
  /**
   * Returns the response from the api
   * @param {ProductFrequency|int} frequency
   * @returns {String} the frequency formatted for the user to look at.
   */
  frequencyString(frequency) {
    return `${frequency.frequency} MHz`;
  },
  /**
   * Returns the response from the api
   * @param {ProductFrequency|int} frequency
   * @returns {String} the weight formatted for the user to look at.
   */
  weightString(frequency) {
    if (frequency.weight) {
      return `${frequency.weight} g`;
    }
    return 'N/A'
  },
  /**
   * Returns the response from the api
   * @param {ProductFrequency|int} frequency
   * @returns {String} the created_at date formatted for the user to look at.
   */
  date(frequency) {
    const dateObj = new Date(frequency.created_at);
    const dateString = dateObj.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric', hour: 'numeric', minute: 'numeric' });
    return `${dateString}`;
  },
};

export const cyclopediaHelpers = {
  getLocalization(cyclopediaLocalizations, language, cyclopediaId) {
    if (!isNaN(language)) {
      language = { language_id: language };
    }
    // it is very likely that the cyclopedia doesn't have a localization for each language
    const matches = cyclopediaLocalizations.filter((l) => l.language_id === language.language_id);
    if (matches.length > 0) {
      return new types.CyclopediaLocalization(matches[0]);
    }
    return new types.CyclopediaLocalization({ cyclopedia_id: cyclopediaId, language_id: language.language_id, });
  },
  getPreferredName(cyclopedia) {
    try {
      let localization = this.getLocalization(cyclopedia.localizations, 1, cyclopedia.id);
      return localization.name || cyclopedia.name;
    } catch (e) {
      return cyclopedia.name;
    }
  },
  getPreferredDescription(cyclopedia) {
    try {
      let localization = this.getLocalization(cyclopedia.localizations, 1, cyclopedia.id);
      return localization.description || cyclopedia.description;
    } catch (e) {
      return cyclopedia.description;
    }
  },
  companiesString(cyclopedia) {
    const companies = JSON.parse(JSON.stringify(cyclopedia.companies));
    const maxCompanyCount = 3;
    let string = companies.slice(0, maxCompanyCount).map((company) => company.name).join(', ');

    if (companies.length - maxCompanyCount > 0) {
      string += `, +${companies.length - maxCompanyCount} more`;
    }

    return string;
  },
  analysesString(cyclopedia) {
    const analyses = JSON.parse(JSON.stringify(cyclopedia.analyses));
    const maxCompanyCount = 1;
    let string = analyses.slice(0, maxCompanyCount).map((analysis) => analysis.name).join(', ');

    if (analyses.length - maxCompanyCount > 0) {
      string += `, +${analyses.length - maxCompanyCount} more`;
    }

    return string;
  },
  getImage(cyclopedia, type = 'product', size = 'regular') {
    try {
      return cyclopedia.images.filter((image) => {
        return image.image.image_type.name === type;
      }).filter((image) => {
        return image.image.image_size.name === size;
      })[0].image.url;
    } catch (e) {
      try {
        return cyclopedia.images.filter((image) => {
          return image.image.image_type.name === type;
        })[0].image.url;
      } catch (e) {
        if (type === 'product') return 'https://www.revereelectric.com/ASSETS/WEB_THEMES//REVERE_ELECTRIC/images/NoImage.png';
        else return 'https://www.revereelectric.com/ASSETS/WEB_THEMES//REVERE_ELECTRIC/images/NoImage.png';
      }
    }
  },
};

export const cyclopediaAnalysisHelpers = {
  getLocalization(analysis, language) {
    return this.getLocalizationFromList(analysis.localizations, language, analysis.id)
  },
  getLocalizationFromList(localizations, language, analysisId) {
    if (!isNaN(language)) {
      language = { language_id: language };
    }
    // it is very likely that the cyclopedia doesn't have a localization for each language
    const matches = localizations.filter((l) => l.language_id === language.language_id);
    if (matches.length > 0) {
      return new types.CyclopediaAnalysisLocalization(matches[0]);
    }
    return new types.CyclopediaAnalysisLocalization({ cyclopedia_analysis_id: analysisId, language_id: language.language_id, });
  },
  getPreferredName(analysis) {
    try {
      let localization = this.getLocalization(analysis, 1, analysis.id);
      return localization.name || analysis.name;
    } catch (e) {
      return analysis.name;
    }
  },
};
export const cyclopediaAnalysisCategoryHelpers = {
  getLocalization(category, language) {
    return this.getLocalizationFromList(category.localizations, language, category.id)
  },
  getLocalizationFromList(localizations, language, categoryId) {
    if (!isNaN(language)) {
      language = { language_id: language };
    }
    // it is very likely that the cyclopedia doesn't have a localization for each language
    const matches = localizations.filter((l) => l.language_id === language.language_id);
    if (matches.length > 0) {
      return new types.CyclopediaAnalysisCategoryLocalization(matches[0]);
    }
    return new types.CyclopediaAnalysisCategoryLocalization({ cyclopedia_analysis_category_id: categoryId, language_id: language.language_id, });
  },
  getPreferredName(category) {
    try {
      let localization = this.getLocalization(category, 1, category.id);
      return localization.name || category.name;
    } catch (e) {
      return category.name;
    }
  },
  getPreferredDescription(category) {
    try {
      let localization = this.getLocalization(category, 1, category.id);
      return localization.description || category.description;
    } catch (e) {
      return category.description;
    }
  },
  getPreferredFreeDescription(category) {
    try {
      let localization = this.getLocalization(category, 1, category.id);
      return localization.free_description || category.free_description;
    } catch (e) {
      return category.free_description;
    }
  },
  getImage(cyclopedia, type = 'hero', size = 'regular') {
    try {
      return cyclopedia.images.filter((image) => {
        return image.image.image_type.name === type;
      }).filter((image) => {
        return image.image.image_size.name === size;
      })[0].image.url;
    } catch (e) {
      try {
        return cyclopedia.images.filter((image) => {
          return image.image.image_type.name === type;
        })[0].image.url;
      } catch (e) {
        if (type === 'product') return 'https://www.revereelectric.com/ASSETS/WEB_THEMES//REVERE_ELECTRIC/images/NoImage.png';
        else return 'https://www.revereelectric.com/ASSETS/WEB_THEMES//REVERE_ELECTRIC/images/NoImage.png';
      }
    }
  },
};

export const userHelpers = {
  getCountries() {
    return countries;
  },
  getCountriesWithStates() {
    return countriesWithStates;
  },
  getStates() {
    return states;
  },
};

export const fcmHelpers = {

  /**
   * @param { ComposePushNotificationMessage } message
   * @param { Array<string> } fcmTokens
   * @param { String } notificationType
   * @return {[{fcm: [], data: {}, message_body: string, message_title: string, type: string}]}
   */
   makeSendNotificationMessage (message, fcmTokens, notificationType) {
    let notifications = [];
    let notificationData = {};
    let inAppDataNotification = {
      type: "general",
      data: {
        title: message.title || "",
        body: message.message || ""
      }
    };

    if (message.link) {
      notificationData.webview_url = message.link;
      inAppDataNotification.data.click_action = { open_url: message.link }
    }

    let inAppData = {
      local_notifications: JSON.stringify({
        batch_id: 'console_' + new Date().getTime().toString(),
        notifications: [ inAppDataNotification ]
      })
    }

    if(notificationType === 'notification-data' || notificationType === 'notification') {
      notifications.push(new types.PushNotification({
        type: "notification",
        fcm: fcmTokens,
        data: notificationData,
        message_title: message.title,
        message_body: message.message,
      }));
    }
    if(notificationType === 'notification-data' || notificationType === 'data') {
      notifications.push(new types.PushNotification({
        type: "data",
        fcm: fcmTokens,
        data: inAppData,
        message_title: message.title,
        message_body: message.message,
      }));
    }

    return notifications;
  }
};

export default {
  product: productHelpers,
  user: userHelpers,
  cyclopedia: cyclopediaHelpers,
  cyclopediaAnalysis: cyclopediaAnalysisHelpers,
  cyclopediaAnalysisCategory: cyclopediaAnalysisCategoryHelpers,
  productFrequency: productFrequencyHelpers,
  fcmHelpers:fcmHelpers
}

const countries = [
  {id: 'US', value: 'United States'},
  {id: 'CA', value: 'Canada'},
  {id: 'GB', value: 'United Kingdom'},
  {id: 'GG', value: 'United Kingdom Channel Islands (Guernsey)'},
  {id: 'JE', value: 'United Kingdom Channel Islands (Jersey)'},
  {id: 'AF', value: 'Afghanistan'},
  {id: 'AX', value: 'Åland'},
  {id: 'AL', value: 'Albania'},
  {id: 'DZ', value: 'Algeria'},
  {id: 'AS', value: 'American Samoa'},
  {id: 'AD', value: 'Andorra'},
  {id: 'AO', value: 'Angola'},
  {id: 'AI', value: 'Anguilla'},
  {id: 'AQ', value: 'Antarctica'},
  {id: 'AG', value: 'Antigua and Barbuda'},
  {id: 'AR', value: 'Argentina'},
  {id: 'AM', value: 'Armenia'},
  {id: 'AW', value: 'Aruba'},
  {id: 'AU', value: 'Australia'},
  {id: 'AT', value: 'Austria'},
  {id: 'AZ', value: 'Azerbaijan'},
  {id: 'BS', value: 'Bahamas'},
  {id: 'BH', value: 'Bahrain'},
  {id: 'BD', value: 'Bangladesh'},
  {id: 'BB', value: 'Barbados'},
  {id: 'BY', value: 'Belarus'},
  {id: 'BE', value: 'Belgium'},
  {id: 'BZ', value: 'Belize'},
  {id: 'BJ', value: 'Benin'},
  {id: 'BM', value: 'Bermuda'},
  {id: 'BT', value: 'Bhutan'},
  {id: 'BO', value: 'Bolivia'},
  {id: 'BQ', value: 'Bonaire'},
  {id: 'BA', value: 'Bosnia and Herzegovina'},{id: 'BW', value: 'Botswana'},{id: 'BV', value: 'Bouvet Island'},{id: 'IO', value: 'British Indian Ocean Territory'},{id: 'VG', value: 'British Virgin Islands'},{id: 'BN', value: 'Brunei'},{id: 'BG', value: 'Bulgaria'},{id: 'BF', value: 'Burkina Faso'},{id: 'BI', value: 'Burundi'},{id: 'KH', value: 'Cambodia'},{id: 'CM', value: 'Cameroon'},{id: 'CV', value: 'Cape Verde'},{id: 'KY', value: 'Cayman Islands'},{id: 'CF', value: 'Central African Republic'},{id: 'TD', value: 'Chad'},{id: 'CL', value: 'Chile'},{id: 'CN', value: 'China'},{id: 'CX', value: 'Christmas Island'},{id: 'CC', value: 'Cocos (Keeling) Islands'},{id: 'CO', value: 'Colombia'},{id: 'KM', value: 'Comoros'},{id: 'CK', value: 'Cook Islands'},{id: 'CR', value: 'Costa Rica'},{id: 'HR', value: 'Croatia (Local Name: Hrvatska)'},{id: 'CU', value: 'Cuba'},{id: 'CW', value: 'Curacao'},{id: 'CY', value: 'Cyprus'},{id: 'CZ', value: 'Czech Republic'},{id: 'CD', value: 'Democratic Republic of the Congo'},{id: 'DK', value: 'Denmark'},{id: 'DJ', value: 'Djibouti'},{id: 'DM', value: 'Dominica'},{id: 'DO', value: 'Dominican Republic'},{id: 'TL', value: 'East Timor'},{id: 'EG', value: 'Egypt'},{id: 'SV', value: 'El Salvador'},{id: 'GQ', value: 'Equatorial Guinea'},{id: 'ER', value: 'Eritrea'},{id: 'EE', value: 'Estonia'},{id: 'ET', value: 'Ethiopia'},{id: 'FK', value: 'Falkland Islands (Malvinas)'},{id: 'FO', value: 'Faroe Islands'},{id: 'FJ', value: 'Fiji'},{id: 'FI', value: 'Finland'},{id: 'FR', value: 'France'},{id: 'GF', value: 'French Guiana'},{id: 'PF', value: 'French Polynesia'},{id: 'TF', value: 'French Southern Territories'},{id: 'GA', value: 'Gabon'},{id: 'GM', value: 'Gambia'},{id: 'GE', value: 'Georgia'},{id: 'DE', value: 'Germany'},{id: 'GH', value: 'Ghana'},{id: 'GI', value: 'Gibraltar'},{id: 'GR', value: 'Greece'},{id: 'GL', value: 'Greenland'},{id: 'GD', value: 'Grenada'},{id: 'GP', value: 'Guadeloupe'},{id: 'GU', value: 'Guam'},{id: 'GT', value: 'Guatemala'},{id: 'GG', value: 'Guernsey'},{id: 'GN', value: 'Guinea'},{id: 'GW', value: 'Guinea-Bissau'},{id: 'GY', value: 'Guyana'},{id: 'HT', value: 'Haiti'},{id: 'HM', value: 'Heard Island and McDonald Islands'},{id: 'HN', value: 'Honduras'},{id: 'HK', value: 'Hong Kong'},{id: 'HU', value: 'Hungary'},{id: 'IS', value: 'Iceland'},{id: 'IN', value: 'India'},{id: 'ID', value: 'Indonesia'},{id: 'IR', value: 'Iran'},{id: 'IQ', value: 'Iraq'},{id: 'IE', value: 'Ireland'},{id: 'GB', value: 'Isle of Man'},{id: 'IL', value: 'Israel'},{id: 'IT', value: 'Italy'},{id: 'CI', value: 'Ivory Coast'},{id: 'JM', value: 'Jamaica'},{id: 'JP', value: 'Japan'},{id: 'JE', value: 'Jersey'},{id: 'JO', value: 'Jordan'},{id: 'KZ', value: 'Kazakhstan'},{id: 'KE', value: 'Kenya'},{id: 'KI', value: 'Kiribati'},{id: 'XK', value: 'Kosovo'},{id: 'KW', value: 'Kuwait'},{id: 'KG', value: 'Kyrgyzstan'},{id: 'LA', value: 'Laos'},{id: 'LV', value: 'Latvia'},{id: 'LB', value: 'Lebanon'},{id: 'LS', value: 'Lesotho'},{id: 'LR', value: 'Liberia'},{id: 'LY', value: 'Libya'},{id: 'LI', value: 'Liechtenstein'},{id: 'LT', value: 'Lithuania'},{id: 'LU', value: 'Luxembourg'},{id: 'MO', value: 'Macao'},{id: 'MK', value: 'Macedonia'},{id: 'MG', value: 'Madagascar'},{id: 'MW', value: 'Malawi'},{id: 'MY', value: 'Malaysia'},{id: 'MV', value: 'Maldives'},{id: 'ML', value: 'Mali'},{id: 'MT', value: 'Malta'},{id: 'MH', value: 'Marshall Islands'},{id: 'MQ', value: 'Martinique'},{id: 'MR', value: 'Mauritania'},{id: 'MU', value: 'Mauritius'},{id: 'YT', value: 'Mayotte'},{id: 'MX', value: 'Mexico'},{id: 'FM', value: 'Micronesia'},{id: 'MD', value: 'Moldova'},{id: 'MC', value: 'Monaco'},{id: 'MN', value: 'Mongolia'},{id: 'ME', value: 'Montenegro'},{id: 'MS', value: 'Montserrat'},{id: 'MA', value: 'Morocco'},{id: 'MZ', value: 'Mozambique'},{id: 'MM', value: 'Myanmar (Burma)'},{id: 'NA', value: 'Namibia'},{id: 'NR', value: 'Nauru'},{id: 'NP', value: 'Nepal'},{id: 'NL', value: 'Netherlands'},{id: 'NC', value: 'New Caledonia'},{id: 'NZ', value: 'New Zealand'},{id: 'NI', value: 'Nicaragua'},{id: 'NE', value: 'Niger'},{id: 'NG', value: 'Nigeria'},{id: 'NU', value: 'Niue'},{id: 'NF', value: 'Norfolk Island'},{id: 'KP', value: 'North Korea'},{id: 'MP', value: 'Northern Mariana Islands'},{id: 'NO', value: 'Norway'},{id: 'OM', value: 'Oman'},{id: 'PK', value: 'Pakistan'},{id: 'PW', value: 'Palau'},{id: 'PS', value: 'Palestine'},{id: 'PA', value: 'Panama'},{id: 'PG', value: 'Papua New Guinea'},{id: 'PY', value: 'Paraguay'},{id: 'PE', value: 'Peru'},{id: 'PH', value: 'Philippines'},{id: 'PN', value: 'Pitcairn Islands'},{id: 'PL', value: 'Poland'},{id: 'PT', value: 'Portugal'},{id: 'PR', value: 'Puerto Rico'},{id: 'QA', value: 'Qatar'},{id: 'CG', value: 'Republic of the Congo'},{id: 'RE', value: 'Réunion'},{id: 'RO', value: 'Romania'},{id: 'RU', value: 'Russia'},{id: 'RW', value: 'Rwanda'},{id: 'BL', value: 'Saint Barthélemy'},{id: 'SH', value: 'Saint Helena'},{id: 'KN', value: 'Saint Kitts and Nevis'},{id: 'LC', value: 'Saint Lucia'},{id: 'MF', value: 'Saint Martin'},{id: 'PM', value: 'Saint Pierre and Miquelon'},{id: 'VC', value: 'Saint Vincent and the Grenadines'},{id: 'WS', value: 'Samoa'},{id: 'SM', value: 'San Marino'},{id: 'SA', value: 'Saudi Arabia'},{id: 'SN', value: 'Senegal'},{id: 'RS', value: 'Serbia'},{id: 'SC', value: 'Seychelles'},{id: 'SL', value: 'Sierra Leone'},{id: 'SG', value: 'Singapore'},{id: 'SX', value: 'Sint Maarten'},{id: 'SK', value: 'Slovakia (Slovak Republic)'},{id: 'SI', value: 'Slovenia'},{id: 'SB', value: 'Solomon Islands'},{id: 'SO', value: 'Somalia'},{id: 'ZA', value: 'South Africa'},{id: 'GS', value: 'South Georgia and the South Sandwich Islands'},{id: 'KR', value: 'South Korea'},{id: 'SS', value: 'South Sudan'},{id: 'ES', value: 'Spain'},{id: 'IC', value: 'Spain (Canary Islands)'},{id: 'LK', value: 'Sri Lanka'},{id: 'SD', value: 'Sudan'},{id: 'SR', value: 'Suriname'},{id: 'SJ', value: 'Svalbard and Jan Mayen'},{id: 'SZ', value: 'Swaziland'},{id: 'SE', value: 'Sweden'},{id: 'CH', value: 'Switzerland'},{id: 'SY', value: 'Syria'},{id: 'ST', value: 'São Tomé and Príncipe'},{id: 'TW', value: 'Taiwan'},{id: 'TJ', value: 'Tajikistan'},{id: 'TZ', value: 'Tanzania'},{id: 'TH', value: 'Thailand'},{id: 'TG', value: 'Togo'},{id: 'TK', value: 'Tokelau'},{id: 'TO', value: 'Tonga'},{id: 'TT', value: 'Trinidad and Tobago'},{id: 'TN', value: 'Tunisia'},{id: 'TR', value: 'Turkey'},{id: 'TM', value: 'Turkmenistan'},{id: 'TC', value: 'Turks and Caicos Islands'},{id: 'TV', value: 'Tuvalu'},{id: 'UM', value: 'U.S. Minor Outlying Islands'},{id: 'VI', value: 'U.S. Virgin Islands'},{id: 'UG', value: 'Uganda'},{id: 'UA', value: 'Ukraine'},{id: 'AE', value: 'United Arab Emirates'},{id: 'UY', value: 'Uruguay'},{id: 'UZ', value: 'Uzbekistan'},{id: 'VU', value: 'Vanuatu'},{id: 'VA', value: 'Vatican City'},{id: 'VE', value: 'Venezuela'},{id: 'VN', value: 'Vietnam'},{id: 'WF', value: 'Wallis and Futuna'},{id: 'EH', value: 'Western Sahara'},{id: 'YE', value: 'Yemen'},{id: 'ZM', value: 'Zambia'},{id: 'ZW', value: 'Zimbabwe'}
];

const countriesWithStates = ['US', 'CA', 'AU', 'AF', 'MX', 'NL', 'GB', 'CN'];

const states = {
  "US" : [
    {id: 'AL', value: 'Alabama'},{id: 'AK', value: 'Alaska'},{id: 'AZ', value: 'Arizona'},{id: 'AR', value: 'Arkansas'},{id: 'CA', value: 'California'},{id: 'CO', value: 'Colorado'},{id: 'CT', value: 'Connecticut'},{id: 'DE', value: 'Delaware'},{id: 'DC', value: 'District of Columbia'},{id: 'FL', value: 'Florida'},{id: 'GA', value: 'Georgia'},{id: 'HI', value: 'Hawaii'},{id: 'ID', value: 'Idaho'},{id: 'IL', value: 'Illinois'},{id: 'IN', value: 'Indiana'},{id: 'IA', value: 'Iowa'},{id: 'KS', value: 'Kansas'},{id: 'KY', value: 'Kentucky'},{id: 'LA', value: 'Louisiana'},{id: 'ME', value: 'Maine'},{id: 'MD', value: 'Maryland'},{id: 'MA', value: 'Massachusetts'},{id: 'MI', value: 'Michigan'},{id: 'MN', value: 'Minnesota'},{id: 'MS', value: 'Mississippi'},{id: 'MO', value: 'Missouri'},{id: 'MT', value: 'Montana'},{id: 'NE', value: 'Nebraska'},{id: 'NV', value: 'Nevada'},{id: 'NH', value: 'New Hampshire'},{id: 'NJ', value: 'New Jersey'},{id: 'NM', value: 'New Mexico'},{id: 'NY', value: 'New York'},{id: 'NC', value: 'North Carolina'},{id: 'ND', value: 'North Dakota'},{id: 'OH', value: 'Ohio'},{id: 'OK', value: 'Oklahoma'},{id: 'OR', value: 'Oregon'},{id: 'PA', value: 'Pennsylvania'},{id: 'RI', value: 'Rhode Island'},{id: 'SC', value: 'South Carolina'},{id: 'SD', value: 'South Dakota'},{id: 'TN', value: 'Tennessee'},{id: 'TX', value: 'Texas'},{id: 'UT', value: 'Utah'},{id: 'VT', value: 'Vermont'},{id: 'VA', value: 'Virginia'},{id: 'WA', value: 'Washington'},{id: 'WV', value: 'West Virginia'},{id: 'WI', value: 'Wisconsin'},{id: 'WY', value: 'Wyoming'}
  ],
  "CA" : [
    {id: 'AB', value: 'Alberta'},{id: 'BC', value: 'British Columbia'},{id: 'MB', value: 'Manitoba'},{id: 'NB', value: 'New Brunswick'},{id: 'NL', value: 'Newfoundland and Labrador'},{id: 'NS', value: 'Nova Scotia'},{id: 'NT', value: 'Northwest Territories'},{id: 'NU', value: 'Nunavut'},{id: 'ON', value: 'Ontario'},{id: 'PE', value: 'Prince Edward Island'},{id: 'QC', value: 'Quebec'},{id: 'SK', value: 'Saskatchewan'},{id: 'YT', value: 'Yukon'}
  ],
  "AU" : [
    {'id':'Ashmore and Cartier Islands', 'value':'Ashmore and Cartier Islands'},
    {'id':'Australian Antarctic Territory', 'value':'Australian Antarctic Territory'},
    {'id':'ACT', 'value':'Australian Capital Territory'},
    {'id':'CX', 'value':'Christmas Island'},
    {'id':'CC', 'value':'Cocos Islands'},
    {'id':'Coral Sea Islands', 'value':'Coral Sea Islands'},
    {'id':'HM', 'value':'Heard Island and McDonald Islands'},
    {'id':'JBT', 'value':'Jervis Bay Territory'},
    {'id':'NSW', 'value':'New South Wales'},
    {'id':'NF', 'value':'Norfolk Island'},
    {'id':'NT', 'value':'Northern Territory'},
    {'id':'QLD', 'value':'Queensland'},
    {'id':'SA', 'value':'South Australia'},
    {'id':'TAS', 'value':'Tasmania'},
    {'id':'VIC', 'value':'Victoria'},
    {'id':'WA', 'value':'Western Australia'}
  ],
  "AF" : [
    {id: 'GP', value: '(AF) Gauteng'},{id: 'WP', value: '(AF) Western Cape'},{id: 'EC', value: '(AF) Eastern Cape'},{id: 'KZN', value: '(AF) KwaZulu Natal'},{id: 'NW', value: '(AF) North West'},{id: 'AF_NC', value: '(AF) Northern Cape'},{id: 'MP', value: '(AF) Mpumalanga'},{id: 'FS', value: '(AF) Free State'}
  ],
  "MX" : [
    {'value':'Aguascalientes','id':'AG'},
    {'value':'Baja California','id':'BC'},
    {'value':'Baja California Sur','id':'BS'},
    {'value':'Campeche','id':'CM'},
    {'value':'Chiapas','id':'CS'},
    {'value':'Chihuahua','id':'CH'},
    {'value':'Coahuila','id':'COA'},
    {'value':'Colima','id':'CL'},
    {'value':'Federal District','id':'DF'},
    {'value':'Durango','id':'DG'},
    {'value':'Guanajuato','id':'GT'},
    {'value':'Guerrero','id':'GR'},
    {'value':'Hidalgo','id':'HG'},
    {'value':'Jalisco','id':'JA'},
    {'value':'Mexico','id':'ME'},
    {'value':'Michoacán','id':'MI'},
    {'value':'Morelos','id':'MO'},
    {'value':'Nayarit','id':'NA'},
    {'value':'Nuevo León','id':'NL'},
    {'value':'Oaxaca','id':'OA'},
    {'value':'Puebla','id':'PU'},
    {'value':'Querétaro','id':'QE'},
    {'value':'Quintana Roo','id':'QR'},
    {'value':'San Luis Potosí','id':'SL'},
    {'value':'Sinaloa','id':'SI'},
    {'value':'Sonora','id':'SO'},
    {'value':'Tabasco','id':'TB'},
    {'value':'Tamaulipas','id':'TM'},
    {'value':'Tlaxcala','id':'TL'},
    {'value':'Veracruz','id':'VE'},
    {'value':'Yucatán','id':'YU'},
    {'value':'Zacatecas','id':'ZA'}
  ],
  "NL" : [
    {'id':'DR', 'value':'Drenthe','country':'NL'},
    {'id':'FL', 'value':'Flevoland','country':'NL'},
    {'id':'FR', 'value':'Friesland','country':'NL','alt':['Fryslân']},
    {'id':'GD', 'value':'Gelderland','country':'NL'},
    {'id':'GR', 'value':'Groningen','country':'NL'},
    {'id':'LB', 'value':'Limburg','country':'NL'},
    {'id':'NB', 'value':'Noord-Brabant','country':'NL'},
    {'id':'NH', 'value':'Noord-Holland','country':'NL'},
    {'id':'OV', 'value':'Overijssel','country':'NL'},
    {'id':'UT', 'value':'Utrecht','country':'NL'},
    {'id':'ZH', 'value':'Zuid-Holland','country':'NL'},
    {'id':'ZL', 'value':'Zeeland','country':'NL'}
  ],
  "GB" : [
    { 'value': 'Avon', 'country': 'GB', 'region': 'England' },
    { 'value': 'Bedfordshire', 'country': 'GB', 'region': 'England' },
    { 'value': 'Berkshire', 'country': 'GB', 'region': 'England' },
    { 'value': 'Borders', 'country': 'GB', 'region': 'England' },
    { 'value': 'Bristol', 'country': 'GB', 'region': 'England' },
    { 'value': 'Buckinghamshire', 'country': 'GB', 'region': 'England' },
    { 'value': 'Cambridgeshire', 'country': 'GB', 'region': 'England' },
    { 'value': 'Cheshire', 'country': 'GB', 'region': 'England' },
    { 'value': 'Cleveland', 'country': 'GB', 'region': 'England' },
    { 'value': 'Cornwall', 'country': 'GB', 'region': 'England' },
    { 'value': 'Cumbria', 'country': 'GB', 'region': 'England' },
    { 'value': 'Derbyshire', 'country': 'GB', 'region': 'England' },
    { 'value': 'Devon', 'country': 'GB', 'region': 'England' },
    { 'value': 'Dorset', 'country': 'GB', 'region': 'England' },
    { 'value': 'Durham', 'country': 'GB', 'region': 'England' },
    { 'value': 'East Riding of Yorkshire', 'country': 'GB', 'region': 'England' },
    { 'value': 'East Sussex', 'country': 'GB', 'region': 'England' },
    { 'value': 'Essex', 'country': 'GB', 'region': 'England' },
    { 'value': 'Gloucestershire', 'country': 'GB', 'region': 'England' },
    { 'value': 'Greater Manchester', 'country': 'GB', 'region': 'England' },
    { 'value': 'Hampshire', 'country': 'GB', 'region': 'England' },
    { 'value': 'Herefordshire', 'country': 'GB', 'region': 'England' },
    { 'value': 'Hertfordshire', 'country': 'GB', 'region': 'England' },
    { 'value': 'Humberside', 'country': 'GB', 'region': 'England' },
    { 'value': 'Isle of Man', 'country': 'GB', 'region': 'England' },
    { 'value': 'Isle of Wight', 'country': 'GB', 'region': 'England' },
    { 'value': 'Isles of Scilly', 'country': 'GB', 'region': 'England' },
    { 'value': 'Kent', 'country': 'GB', 'region': 'England' },
    { 'value': 'Lancashire', 'country': 'GB', 'region': 'England' },
    { 'value': 'Leicestershire', 'country': 'GB', 'region': 'England' },
    { 'value': 'Lincolnshire', 'country': 'GB', 'region': 'England' },
    { 'value': 'London', 'country': 'GB', 'region': 'England' },
    { 'value': 'Merseyside', 'country': 'GB', 'region': 'England' },
    { 'value': 'Middlesex', 'country': 'GB', 'region': 'England' },
    { 'value': 'Norfolk', 'country': 'GB', 'region': 'England' },
    { 'value': 'North Yorkshire', 'country': 'GB', 'region': 'England' },
    { 'value': 'Northamptonshire', 'country': 'GB', 'region': 'England' },
    { 'value': 'Northumberland', 'country': 'GB', 'region': 'England' },
    { 'value': 'Nottinghamshire', 'country': 'GB', 'region': 'England' },
    { 'value': 'Oxfordshire', 'country': 'GB', 'region': 'England' },
    { 'value': 'Rutland', 'country': 'GB', 'region': 'England' },
    { 'value': 'Shropshire', 'country': 'GB', 'region': 'England' },
    { 'value': 'Somerset', 'country': 'GB', 'region': 'England' },
    { 'value': 'South Yorkshire', 'country': 'GB', 'region': 'England' },
    { 'value': 'Staffordshire', 'country': 'GB', 'region': 'England' },
    { 'value': 'Suffolk', 'country': 'GB', 'region': 'England' },
    { 'value': 'Surrey', 'country': 'GB', 'region': 'England' },
    { 'value': 'Tyne and Wear', 'country': 'GB', 'region': 'England' },
    { 'value': 'Warwickshire', 'country': 'GB', 'region': 'England' },
    { 'value': 'West Midlands', 'country': 'GB', 'region': 'England' },
    { 'value': 'West Sussex', 'country': 'GB', 'region': 'England' },
    { 'value': 'West Yorkshire', 'country': 'GB', 'region': 'England' },
    { 'value': 'Wiltshire', 'country': 'GB', 'region': 'England' },
    { 'value': 'Worcestershire', 'country': 'GB', 'region': 'England' },

    { 'value': 'Antrim', 'country': 'GB', 'region': 'Northern Ireland' },
    { 'value': 'Down', 'country': 'GB', 'region': 'Northern Ireland' },
    { 'value': 'Fermanagh', 'country': 'GB', 'region': 'Northern Ireland' },
    { 'value': 'Londonderry', 'country': 'GB', 'region': 'Northern Ireland' },
    { 'value': 'Tyrone', 'country': 'GB', 'region': 'Northern Ireland' },

    { 'value': 'Aberdeen City', 'country': 'GB', 'region': 'Scotland' },
    { 'value': 'Aberdeenshire', 'country': 'GB', 'region': 'Scotland' },
    { 'value': 'Angus', 'country': 'GB', 'region': 'Scotland' },
    { 'value': 'Argyll and Bute', 'country': 'GB', 'region': 'Scotland' },
    { 'value': 'Armagh', 'country': 'GB', 'region': 'Scotland' },
    { 'value': 'Carmarthenshire', 'country': 'GB', 'region': 'Scotland' },
    { 'value': 'Clackmannan', 'country': 'GB', 'region': 'Scotland' },
    { 'value': 'Dumfries and Galloway', 'country': 'GB', 'region': 'Scotland' },
    { 'value': 'East Ayrshire', 'country': 'GB', 'region': 'Scotland' },
    { 'value': 'East Dunbartonshire', 'country': 'GB', 'region': 'Scotland' },
    { 'value': 'East Lothian', 'country': 'GB', 'region': 'Scotland' },
    { 'value': 'East Renfrewshire', 'country': 'GB', 'region': 'Scotland' },
    { 'value': 'Edinburgh City', 'country': 'GB', 'region': 'Scotland' },
    { 'value': 'Falkirk', 'country': 'GB', 'region': 'Scotland' },
    { 'value': 'Fife', 'country': 'GB', 'region': 'Scotland' },
    { 'value': 'Glasgow', 'country': 'GB', 'region': 'Scotland' },
    { 'value': 'Highland', 'country': 'GB', 'region': 'Scotland' },
    { 'value': 'Inverclyde', 'country': 'GB', 'region': 'Scotland' },
    { 'value': 'Midlothian', 'country': 'GB', 'region': 'Scotland' },
    { 'value': 'Moray', 'country': 'GB', 'region': 'Scotland' },
    { 'value': 'North Ayrshire', 'country': 'GB', 'region': 'Scotland' },
    { 'value': 'North Lanarkshire', 'country': 'GB', 'region': 'Scotland' },
    { 'value': 'Orkney', 'country': 'GB', 'region': 'Scotland' },
    { 'value': 'Perthshire and Kinross', 'country': 'GB', 'region': 'Scotland' },
    { 'value': 'Renfrewshire', 'country': 'GB', 'region': 'Scotland' },
    { 'value': 'Roxburghshire', 'country': 'GB', 'region': 'Scotland' },
    { 'value': 'Shetland', 'country': 'GB', 'region': 'Scotland' },
    { 'value': 'South Ayrshire', 'country': 'GB', 'region': 'Scotland' },
    { 'value': 'South Lanarkshire', 'country': 'GB', 'region': 'Scotland' },
    { 'value': 'Stirling', 'country': 'GB', 'region': 'Scotland' },
    { 'value': 'West Dunbartonshire', 'country': 'GB', 'region': 'Scotland' },
    { 'value': 'West Lothian', 'country': 'GB', 'region': 'Scotland' },
    { 'value': 'Western Isles', 'country': 'GB', 'region': 'Scotland' },

    { 'value': 'Blaenau Gwent', 'country': 'GB', 'region': 'Wales' },
    { 'value': 'Bridgend', 'country': 'GB', 'region': 'Wales' },
    { 'value': 'Caerphilly', 'country': 'GB', 'region': 'Wales' },
    { 'value': 'Cardiff', 'country': 'GB', 'region': 'Wales' },
    { 'value': 'Ceredigion', 'country': 'GB', 'region': 'Wales' },
    { 'value': 'Conwy', 'country': 'GB', 'region': 'Wales' },
    { 'value': 'Denbighshire', 'country': 'GB', 'region': 'Wales' },
    { 'value': 'Flintshire', 'country': 'GB', 'region': 'Wales' },
    { 'value': 'Gwynedd', 'country': 'GB', 'region': 'Wales' },
    { 'value': 'Isle of Anglesey', 'country': 'GB', 'region': 'Wales' },
    { 'value': 'Merthyr Tydfil', 'country': 'GB', 'region': 'Wales' },
    { 'value': 'Monmouthshire', 'country': 'GB', 'region': 'Wales' },
    { 'value': 'Neath Port Talbot', 'country': 'GB', 'region': 'Wales' },
    { 'value': 'Newport', 'country': 'GB', 'region': 'Wales' },
    { 'value': 'Pembrokeshire', 'country': 'GB', 'region': 'Wales' },
    { 'value': 'Powys', 'country': 'GB', 'region': 'Wales' },
    { 'value': 'Rhondda Cynon Taff', 'country': 'GB', 'region': 'Wales' },
    { 'value': 'Swansea', 'country': 'GB', 'region': 'Wales' },
    { 'value': 'The Vale of Glamorgan', 'country': 'GB', 'region': 'Wales' },
    { 'value': 'Torfaen', 'country': 'GB', 'region': 'Wales' },
    { 'value': 'Wrexham', 'country': 'GB', 'region': 'Wales' }
  ],
  "CN" : [
    { "name": "重庆", "short": "渝", "value": "Chongqing", "country": "CN" },
    { "name": "黑龙江", "short": "黑","value": "Heilongjiang", "country": "CN" },
    { "name": "吉林", "short": "吉", "value": "Jilin", "country": "CN" },
    { "name": "海南", "short": "琼", "value": "Hainan", "country": "CN" },
    { "name": "北京", "short": "京","value": "Beijing",  "country": "CN" },
    { "name": "辽宁", "short": "辽", "value": "Liaoning", "country": "CN" },
    { "name": "内蒙古", "short": "蒙", "value": "Inner Mongolia","alt":["Nei Menggu"], "country": "CN" },
    { "name": "西藏", "short": "藏", "value": "Xizang", "alt":["Tibet"], "country": "CN" },
    { "name": "青海", "short": "青", "value": "Qinghai", "country": "CN" },
    { "name": "宁夏", "short": "宁", "value": "Ningxia", "country": "CN" },
    { "name": "新疆", "short": "新", "value": "Xinjiang","alt":["Uygur"], "country": "CN" },
    { "name": "甘肃", "short": "甘", "value": "Gansu", "country": "CN" },
    { "name": "河北", "short": "冀", "value": "Hebei", "country": "CN" },
    { "name": "河南", "short": "豫", "value": "Henan", "country": "CN" },
    { "name": "湖北", "short": "鄂", "value": "Hubei", "country": "CN" },
    { "name": "湖南", "short": "湘", "value": "Hunan", "country": "CN" },
    { "name": "山东", "short": "鲁", "value": "Shandong", "country": "CN" },
    { "name": "江苏", "short": "苏", "value": "Jiangsu", "country": "CN" },
    { "name": "安徽", "short": "皖", "value": "Anhui", "country": "CN" },
    { "name": "山西", "short": "晋", "value": "Shanxi", "country": "CN" },
    { "name": "陕西", "short": "陕", "value": "Shaanxi", "country": "CN" },
    { "name": "四川", "short": "川", "value": "sichuan", "country": "CN" },
    { "name": "云南", "short": "滇", "value": "Yunnan", "country": "CN" },
    { "name": "贵州", "short": "黔", "value": "Guizhou", "country": "CN" },
    { "name": "浙江", "short": "浙", "value": "Zhejiang", "country": "CN" },
    { "name": "福建", "short": "闽", "value": "Fujian", "country": "CN" },
    { "name": "广西", "short": "桂", "value": "Guangxi", "country": "CN" },
    { "name": "上海", "short": "沪", "value": "Shanghai", "country": "CN" },
    { "name": "天津", "short": "津", "value": "Tianjin", "country": "CN" },
    { "name": "香港", "short": "港", "value": "Hongkong", "alt":["Hong Kong"] ,"country": "CN" },
    { "name": "澳门", "short": "澳", "value": "Macau","alt":["Macao"] , "country": "CN" },
    { "name": "台湾", "short": "台", "value": "Taiwan", "country": "CN" },
    { "name": "江西", "short": "赣", "value": "Jiangxi", "country": "CN" },
    { "name": "广东", "short": "粤", "value": "Guangdong", "country": "CN" }
  ]
};
